import type { ReactNode } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { chatBackgroundWorkOf, chatTasksOf } from '@localagent/sdk/react';
import type { ChatSession } from '@localagent/sdk';
import { useResizablePanel } from '@localagent/ui/hooks/use-resizable-panel';
import { tasksPanelHeightAtom, tasksPanelLayoutAtom } from '../../../stores/layout/atoms';
import { SessionTasksPanel } from './SessionTasksPanel';
import { useVisibleRunningWork } from './running-work';

export interface TasksColumnProps {
  sessionId: string | null | undefined;
  chat: ChatSession | null;
  progress: ReactNode | null;
  progressLabel: string;
  testIdPrefix?: string;
}

export function TasksColumn({ sessionId, chat, progress, progressLabel, testIdPrefix = '' }: TasksColumnProps) {
  const tasks = useAtomValue(chatTasksOf(sessionId));
  const work = useAtomValue(chatBackgroundWorkOf(sessionId));
  const visible = useVisibleRunningWork(work);
  const [layout, setLayout] = useAtom(tasksPanelLayoutAtom);
  const [height, setHeight] = useAtom(tasksPanelHeightAtom);
  const resize = useResizablePanel({
    axis: 'vertical',
    handleEdge: 'start',
    size: height,
    onCommit: setHeight,
    min: 120,
    max: 2000,
  });

  if (progress === null) {
    return (
      <div data-testid={`${testIdPrefix}tasks-column`} data-layout="simple" className="flex min-h-0 flex-1 flex-col justify-end p-3">
        <SessionTasksPanel sessionId={sessionId} chat={chat} variant="simple" testIdPrefix={testIdPrefix} />
      </div>
    );
  }

  const hasContent = (tasks?.tasks.length ?? 0) > 0 || visible.running.length > 0 || visible.recent.length > 0;
  const effective = hasContent ? layout : 'collapsed';
  return (
    <div data-testid={`${testIdPrefix}tasks-column`} data-layout={effective} className="flex min-h-0 flex-1 flex-col">
      {effective === 'maximized' ? (
        <button
          type="button"
          data-testid={`${testIdPrefix}progress-bar`}
          aria-expanded={false}
          onClick={() => setLayout('split')}
          className="flex h-10 flex-none items-center justify-between border-b border-border px-3 text-caption text-muted-foreground hover:bg-accent"
        >
          <span>
            <span className="font-semibold text-foreground">Progress</span> · {progressLabel}
          </span>
          <span aria-hidden="true">▾</span>
        </button>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{progress}</div>
      )}
      {effective === 'split' && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize progress and tasks"
          data-testid={`${testIdPrefix}tasks-split-handle`}
          onPointerDown={resize.onHandlePointerDown}
          className="grid h-2.5 flex-none cursor-row-resize touch-none select-none place-items-center border-y border-border bg-card hover:bg-primary/30 active:bg-primary/50"
        >
          <span className="h-0.5 w-9 rounded-full bg-border" />
        </div>
      )}
      <div
        data-testid={`${testIdPrefix}tasks-region`}
        className={effective === 'collapsed' ? 'flex-none border-t border-border p-2' : 'flex min-h-0 flex-col p-2'}
        style={
          effective === 'split'
            ? { height: resize.size, maxHeight: 'calc(100% - 7.5rem)', flex: 'none' }
            : effective === 'maximized'
              ? { flex: '1 1 0%' }
              : undefined
        }
      >
        <SessionTasksPanel sessionId={sessionId} chat={chat} variant="advanced" testIdPrefix={testIdPrefix} />
      </div>
    </div>
  );
}
